import { paths } from '@luxuryescapes/contract-svc-lux-loyalty';

import { EmptyArray } from '~/utils/arrayUtils';
import { isLuxLoyaltyEnabled } from '~/utils/luxLoyalty';

import { request } from '../common';

import { BASE_PATH } from './constants';

type RequestOptions = {
  signal?: AbortSignal;
};

type GetLuxLoyaltyPendingPointsResponse =
  paths['/api/lux-loyalty/customer/{customerId}/order/{orderId}/pending-points']['get']['responses']['200']['content']['application/json'];

type GetLuxLoyaltyCustomerPendingPointsResponse =
  paths['/api/lux-loyalty/customer/{customerId}/pending-points']['get']['responses']['200']['content']['application/json'];

type AdminLuxLoyaltyGetAccrualsByOrderResponse =
  paths['/api/lux-loyalty/admin/earn/order/{orderId}']['get']['responses']['200']['content']['application/json'];

export type LuxLoyaltyAccrual = AdminLuxLoyaltyGetAccrualsByOrderResponse['result']['accruals'];

export type CalculateEarnRequest =
  paths['/api/lux-loyalty/points/calculate-earn']['post']['requestBody']['content']['application/json'];

export type CalculateEarnResponse =
  paths['/api/lux-loyalty/points/calculate-earn']['post']['responses']['200']['content']['application/json'];

export type PointsAdjustmentRequestBody =
  paths['/api/lux-loyalty/points/adjustments']['post']['requestBody']['content']['application/json'];

export type PointsAdjustmentResponse =
  paths['/api/lux-loyalty/points/adjustments']['post']['responses']['200']['content']['application/json'];

/** Per-item counts returned by the bq-import endpoint when reconstructing pending points */
export type ReconstructPendingPointsResult =
  paths['/api/lux-loyalty/pending-points/bq-import']['post']['responses']['200']['content']['application/json']['result'];

// ============================================
// Admin partner earn credit + transaction search
// ============================================

const CREDIT_PATH = '/api/lux-loyalty/partners/v1/partners/{partnerId}/points/credit';
const SEARCH_PATH = '/api/lux-loyalty/partners/v1/partners/{partnerId}/transactions/search';

/**
 * Body of the admin partner credit endpoint.
 *
 * Two deliberate differences from the partner-facing credit body: `idempotencyKey` is a field
 * rather than an `idempotency-key` header, and `source` carries no `name`. Both exist because the
 * gateway's global CORS allowlist has neither `idempotency-key` nor `x-api-key-id`, so admin names
 * the partner in the path and the service fills in the source name from that partner.
 */
export type AdminPartnerCreditBody = paths[typeof CREDIT_PATH]['post']['requestBody']['content']['application/json'];

export type AdminPartnerCreditResponse =
  paths[typeof CREDIT_PATH]['post']['responses']['202']['content']['application/json'];

export type AdminPartnerTransactionSearchResponse =
  paths[typeof SEARCH_PATH]['post']['responses']['200']['content']['application/json'];

/**
 * Filters for the admin transaction search. The body is validated with `z.strictObject`, so an
 * unknown field is a 400 rather than being ignored.
 *
 * `Partial` because the generated type describes the *parsed* body, not the request: the validator
 * gives page, pageSize, sortBy, sortOrder and each id filter a `prefault`, so the server supplies
 * them when they are absent and a caller need only send the filters it cares about.
 */
export type AdminPartnerTransactionSearchBody = Partial<
  paths[typeof SEARCH_PATH]['post']['requestBody']['content']['application/json']
>;

/** One transaction as the admin search returns it. Carries `errorMessage`, which partners never see. */
export type AdminPartnerTransaction = AdminPartnerTransactionSearchResponse['result']['transactions'][number];

/** Counts describe the whole filtered match, not the current page, and ignore the status filter. */
export type AdminPartnerTransactionCounts = AdminPartnerTransactionSearchResponse['result']['counts'];

export interface PartnerEarnCsvRow {
  rowIndex: number;
  accountNumber: number;
  activityTitle: string;
  reason: string;
  basePoints?: number;
  statusCredits?: number;
  sourceKey?: string;
}

export interface PartnerEarnRowResult {
  accountNumber: number;
  success: boolean;
  /** Lets the result screen ask the search endpoint how this row actually turned out. */
  idempotencyKey?: string;
  /** The service already had this exact credit (409). Accepted, but nothing new was credited. */
  alreadyCredited?: boolean;
  transactionId?: string;
  error?: string;
  activityTitle?: string;
  reason?: string;
  basePoints?: number;
  statusCredits?: number;
  sourceKey?: string;
}

export interface PartnerEarnBulkResult {
  totalProcessed: number;
  successCount: number;
  /** Rows the service answered with a 409 — counted apart from newly queued credits. */
  alreadyCreditedCount: number;
  failedCount: number;
  results: Array<PartnerEarnRowResult>;
}

export async function calculateEarnPoints(params: CalculateEarnRequest): Promise<CalculateEarnResponse> {
  return request(`${BASE_PATH}/points/calculate-earn`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Creates a points adjustment (credit or debit) for a customer account.
 * Used by CS to manually allocate points when the automated accrual process has failed.
 */
export function createPointsAdjustment(params: PointsAdjustmentRequestBody): Promise<PointsAdjustmentResponse> {
  return request(`${BASE_PATH}/points/adjustments`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function getLuxLoyaltyOrderPendingPoints(
  customerId: string,
  orderId: string,
): Promise<Array<App.LuxLoyaltyPendingPoint>> {
  if (!isLuxLoyaltyEnabled() || !customerId || !orderId) {
    return EmptyArray;
  }

  const uri = `${BASE_PATH}/customer/${customerId}/order/${orderId}/pending-points`;
  const response: GetLuxLoyaltyPendingPointsResponse = await request(uri, {
    method: 'GET',
  });

  return response.result.pendingPoints.map((pendingPointData): App.LuxLoyaltyPendingPoint => {
    const basePoints = pendingPointData.points;
    const bonusPoints = pendingPointData.bonusPoints;

    return {
      id: pendingPointData.id,
      createdAt: pendingPointData.createdAt,
      itemId: pendingPointData.itemId,
      orderId: pendingPointData.orderId,
      basePoints,
      bonusPoints,
      points: basePoints + bonusPoints,
      statusCredits: pendingPointData.statusCredits,
      bonusStatusCredits: pendingPointData.bonusStatusCredits,
      pointsEarnDate: pendingPointData.pointsEarnDate,
      status: pendingPointData.status,
      numberOfNights: pendingPointData.numberOfNights ?? null,
    };
  });
}

export async function getLuxLoyaltyCustomerPendingPoints(
  customerId: string,
): Promise<Array<App.LuxLoyaltyPendingPoint>> {
  const uri = `${BASE_PATH}/customer/${customerId}/pending-points`;
  const response: GetLuxLoyaltyCustomerPendingPointsResponse = await request(uri, { method: 'GET' });
  const { result } = response;

  return result.pendingPoints.map((pendingPointData): App.LuxLoyaltyPendingPoint => {
    const basePoints = pendingPointData.points;
    const bonusPoints = pendingPointData.bonusPoints;

    return {
      id: pendingPointData.id,
      createdAt: pendingPointData.createdAt,
      itemId: pendingPointData.itemId,
      orderId: pendingPointData.orderId,
      basePoints,
      bonusPoints,
      points: basePoints + bonusPoints,
      statusCredits: pendingPointData.statusCredits,
      bonusStatusCredits: pendingPointData.bonusStatusCredits,
      pointsEarnDate: pendingPointData.pointsEarnDate ?? undefined,
      status: pendingPointData.status,
      numberOfNights: pendingPointData.numberOfNights ?? null,
    };
  });
}

export async function getLuxLoyaltySuccessfulAccrualOnOrder(
  orderId: string,
): Promise<Array<App.LuxLoyaltyAccrualItem>> {
  if (!isLuxLoyaltyEnabled() || !orderId) {
    return EmptyArray;
  }

  const uri = `${BASE_PATH}/admin/earn/order/${orderId}`;
  const response: AdminLuxLoyaltyGetAccrualsByOrderResponse = await request(uri, { method: 'GET' });
  return response.result.accruals.map((accrual) => ({
    itemId: accrual.itemId,
    points: accrual.points,
    statusCredits: accrual.statusCredits,
    dateEarned: accrual.dateEarned,
  }));
}

/**
 * Triggers a BigQuery-based reconstruction of the pending points record for a specific order item.
 * Calls POST /api/lux-loyalty/pending-points/bq-import on svc-lux-loyalty.
 *
 * The Content-Type header is required: the shared `request` helper does not set one, and without it
 * the browser sends text/plain, which the service's body parser skips — leaving the payload undefined.
 */
export async function reconstructPendingPoints(
  orderId: string,
  itemId: string,
): Promise<ReconstructPendingPointsResult> {
  const response = await request(`${BASE_PATH}/pending-points/bq-import`, {
    method: 'POST',
    // Reconstruction only fills in a missing record; re-estimating an existing one is a separate concern.
    body: JSON.stringify({ orderId, itemId, reestimate: false }),
    headers: { 'Content-Type': 'application/json' },
  });
  return response.result as ReconstructPendingPointsResult;
}

/**
 * Credits points on behalf of an earn partner.
 *
 * Admin names the partner by id in the path rather than authenticating as it: the partner routes
 * identify the caller by `x-api-key-id`, which the browser cannot send because the gateway's global
 * CORS allowlist carries neither that header nor `idempotency-key`. This endpoint takes the
 * idempotency key in the body for the same reason, and sets `source.name` from the partner itself.
 */
export async function creditPartnerEarn(
  partnerId: string,
  params: AdminPartnerCreditBody,
  options: RequestOptions = {},
): Promise<AdminPartnerCreditResponse> {
  const { signal } = options;
  return request(`${BASE_PATH}/partners/v1/partners/${partnerId}/points/credit`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
    ...(signal ? { signal } : {}),
  });
}

/**
 * Searches one partner's transactions, scoped server-side to `partnerId`.
 *
 * POST rather than GET because a bulk upload's key list does not fit a query string. The bulk flow
 * searches by `idempotencyKeys`: a resubmitted row answers 409 with no transaction id, and the ids
 * from the original 202s are gone once the tab reloads, but the keys are re-derivable from the rows.
 */
export async function searchPartnerTransactions(
  partnerId: string,
  filters: AdminPartnerTransactionSearchBody,
  options: RequestOptions = {},
): Promise<AdminPartnerTransactionSearchResponse> {
  const { signal } = options;
  return request(`${BASE_PATH}/partners/v1/partners/${partnerId}/transactions/search`, {
    method: 'POST',
    body: JSON.stringify(filters),
    headers: { 'Content-Type': 'application/json' },
    ...(signal ? { signal } : {}),
  });
}
